import { type StateCreator } from 'zustand';
import { getNewSortingOptions } from '@pkgs/components/utils/searchStateUtils';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import {
  APARTMENT_SUB_TYPES,
  BusinessArea,
  getEntries,
  getKeys,
  PropertyMarketingType,
  PropertySubType,
  PropertyType,
  type SearchFilterRequestPropertyTypeSubType,
} from '@ev-tech/search-modules-api';

import { DEFAULT_PREFERENCES } from './preferencesSlice';
import { DEFAULT_SORTING_OPTION_ID } from './optionsSlice';
import { type MiddlewareType, type SearchStore } from '../SearchStoreProvider';

export type ObjectTypeCategory = BusinessArea | 'development';

export type PropertyTypeEntry = {
  selected?: boolean;
  subTypes?: PropertySubTypeRecord;
};

export type PropertySubTypeRecord = {
  [_k in PropertySubType]?: {
    selected: boolean;
  };
};

export type PropertyTypeRecord = {
  [_k in keyof SearchFilterRequestPropertyTypeSubType]?: PropertyTypeEntry;
};

export type PropertyTypeState = Record<ObjectTypeCategory, PropertyTypeRecord>;

const orderedResidentialHouseSubTypes = [
  PropertySubType.detached_house,
  PropertySubType.villa,
  PropertySubType.country_house,
  PropertySubType.terraced_house,
  PropertySubType.semi_detached_house,
  PropertySubType.townhouse,
  PropertySubType.apartment_house,
  PropertySubType.special_property,
  PropertySubType.investment,
  PropertySubType.bungalow,
  PropertySubType.castle,
  PropertySubType.other,
] as const;

const orderedResidentialApartmentSubTypes = [
  PropertySubType.flat,
  PropertySubType.penthouse,
  PropertySubType.maisonette,
  PropertySubType.attic,
  PropertySubType.loft,
  PropertySubType.other,
] as const;

const orderedResidentialLandSubTypes = [
  PropertySubType.site,
  PropertySubType.land,
  PropertySubType.parking,
  PropertySubType.other,
] as const;

const orderedCommercialPropertyTypes = [
  PropertyType.investment,
  PropertyType.office,
  PropertyType.store,
  PropertyType.industry,
  PropertyType.land,
  PropertyType.commercial_space,
  PropertyType.hotel,
  PropertyType.agriculture_and_forestry,
  PropertyType.gastronomy,
  PropertyType.other,
] as const;

export const initialPropertyTypeState = {
  residential: {
    house: {
      selected: false,
      subTypes: Object.fromEntries(orderedResidentialHouseSubTypes.map((subType) => [subType, { selected: false }])),
    },
    apartment: {
      selected: false,
      subTypes: Object.fromEntries(orderedResidentialApartmentSubTypes.map((subType) => [subType, { selected: false }])),
    },
    land: {
      selected: false,
      subTypes: Object.fromEntries(orderedResidentialLandSubTypes.map((subType) => [subType, { selected: false }])),
    },
  },
  commercial: Object.fromEntries(orderedCommercialPropertyTypes.map((propertyType) => [propertyType, { selected: false }])),
  development: {},
} satisfies PropertyTypeState;

export type ObjectTypeFilterState = {
  propertyTypeState: PropertyTypeState;
  objectTypeCategory: ObjectTypeCategory;
  marketingType: PropertyMarketingType;
};

type ObjectTypeFilterActions = {
  setMarketingType: (marketingType: PropertyMarketingType) => void;
  setPropertyType: (propertyType: keyof SearchFilterRequestPropertyTypeSubType) => void;
  setPropertySubType: (propertyType: keyof SearchFilterRequestPropertyTypeSubType, propertySubType: PropertySubType) => void;
  setObjectTypeCategory: (objectTypeCategory: ObjectTypeCategory) => void;
};

export type ObjectTypeFilterStore = ObjectTypeFilterState & ObjectTypeFilterActions;

export const DEFAULT_BUSINESS_AREA = BusinessArea.residential;
export const DEFAULT_PROPERTY_MARKETING_TYPE = PropertyMarketingType.sale;

export const defaultObjectTypeFilterState: Readonly<ObjectTypeFilterState> = {
  objectTypeCategory: DEFAULT_BUSINESS_AREA,
  propertyTypeState: initialPropertyTypeState,
  marketingType: DEFAULT_PROPERTY_MARKETING_TYPE,
};

export const createObjectTypeFilterSlice: StateCreator<SearchStore, MiddlewareType, [], ObjectTypeFilterStore> = (set, get) => ({
  ...defaultObjectTypeFilterState,
  setMarketingType: (marketingType: PropertyMarketingType) => {
    set(
      {
        marketingType,
        propertyTypeState: initialPropertyTypeState,
        page: 1,
        preferences: DEFAULT_PREFERENCES,
        ...(get().sortingOptionId === SortingOptionId.PREFERENCES && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
      },
      undefined,
      'setMarketingType',
    );
    const { objectTypeCategory, setObjectTypeCategory, setSortingOptionById } = get();
    if (objectTypeCategory === 'development') {
      setObjectTypeCategory(BusinessArea.residential);
    }
    setSortingOptionById(getNewSortingOptions(marketingType, get().sortingOptionId));
  },
  setPropertyType: (propertyType: keyof SearchFilterRequestPropertyTypeSubType) => {
    set(
      (state) => {
        state.page = 1;
        state.preferences = DEFAULT_PREFERENCES;
        const selectedPropertyType = state.propertyTypeState[state.objectTypeCategory][propertyType];
        if (!selectedPropertyType) return;

        // If property type has subtypes, toggle all subtypes together
        if (selectedPropertyType.subTypes) {
          const subTypeValues = Object.values(selectedPropertyType.subTypes);
          const isAllSubTypesSelected = subTypeValues.every((subType) => subType.selected);

          selectedPropertyType.selected = !isAllSubTypesSelected;
          for (const data of subTypeValues) {
            data.selected = !isAllSubTypesSelected;
          }
        } else {
          selectedPropertyType.selected = !selectedPropertyType.selected;
        }
      },
      undefined,
      'setPropertyType',
    );
  },
  setPropertySubType: (propertyType: keyof SearchFilterRequestPropertyTypeSubType, propertySubType: PropertySubType) => {
    set(
      (state) => {
        state.page = 1;
        state.preferences = DEFAULT_PREFERENCES;
        const selectedPropertyType = state.propertyTypeState[state.objectTypeCategory][propertyType];
        const subType = selectedPropertyType?.subTypes?.[propertySubType];

        if (!subType || !selectedPropertyType.subTypes) return;

        subType.selected = !subType.selected;
        const isAllSubTypesSelected = Object.values(selectedPropertyType.subTypes).every((subType) => subType.selected);
        selectedPropertyType.selected = isAllSubTypesSelected;
      },
      undefined,
      'setPropertySubType',
    );
  },
  setObjectTypeCategory: (objectTypeCategory: ObjectTypeCategory) => {
    set(
      {
        objectTypeCategory,
        preferences: DEFAULT_PREFERENCES,
        page: 1,
      },
      undefined,
      'setObjectTypeCategory',
    );
  },
});

// Extracts all selected property subtypes regardless of parent category
// Caveat: If all subtypes for a property type are selected, no subtypes are included (equivalent to selecting only the parent type)
export function getSelectedSubTypes(filterState: PropertyTypeState) {
  return Object.values(filterState).reduce((acc, propertyType) => {
    const selectedSubTypes = Object.values(propertyType).reduce((subAcc, { subTypes }) => {
      if (subTypes) {
        const allSubTypeKeys = getKeys(subTypes);
        const selectedSubTypeKeys = allSubTypeKeys.filter((subType) => subTypes[subType]?.selected);
        const areAllSubTypesSelected = selectedSubTypeKeys.length === allSubTypeKeys.length;
        if (!areAllSubTypesSelected) {
          subAcc.push(...selectedSubTypeKeys);
        }
      }
      return subAcc;
    }, [] as PropertySubType[]);
    acc.push(...selectedSubTypes);
    return acc;
  }, [] as PropertySubType[]);
}

// Extracts all directly selected property types (not including subtypes)
export function getSelectedPropertyTypes(
  filterState: PropertyTypeState,
  objectTypeCategory: ObjectTypeCategory,
): (keyof SearchFilterRequestPropertyTypeSubType)[] {
  const result = Object.values(filterState).reduce(
    (acc, propertyType) => {
      const selectedPropertyTypes = getEntries(propertyType).reduce(
        (subAcc, [key, data]) => {
          if (data?.selected) {
            subAcc.push(key);
          }
          return subAcc;
        },
        [] as (keyof SearchFilterRequestPropertyTypeSubType)[],
      );
      acc.push(...selectedPropertyTypes);
      return acc;
    },
    [] as (keyof SearchFilterRequestPropertyTypeSubType)[],
  );

  if (objectTypeCategory === 'development') {
    result.push(PropertyType.group);
  }

  return result;
}

export const getSelectedPropertyTypeSubType = (
  propertyTypeSubType?: PropertyTypeState,
  objectTypeCategory?: ObjectTypeCategory,
): SearchFilterRequestPropertyTypeSubType => {
  const result: SearchFilterRequestPropertyTypeSubType = {};

  if (!propertyTypeSubType) return result;

  if (propertyTypeSubType.residential.house?.subTypes) {
    for (const [subType, data] of Object.entries(propertyTypeSubType.residential.house.subTypes)) {
      if (subType === '') continue;
      if (data.selected) {
        result.house = [...(result.house ?? []), subType as PropertySubType];
      }
    }
    if (propertyTypeSubType.residential.house.selected) {
      result.house = [];
    }
  }

  if (propertyTypeSubType.residential.apartment?.subTypes) {
    for (const [subType, data] of Object.entries(propertyTypeSubType.residential.apartment.subTypes)) {
      if (subType === '') continue;
      if (data.selected) {
        result.apartment = [...(result.apartment ?? []), subType as PropertySubType];
      }
    }
    if (propertyTypeSubType.residential.apartment.selected) {
      result.apartment = [];
    }
  }
  if (propertyTypeSubType.residential.land?.subTypes) {
    for (const [subType, data] of Object.entries(propertyTypeSubType.residential.land.subTypes)) {
      if (subType === '') continue;
      if (data.selected) {
        result.land = [...(result.land ?? []), subType as PropertySubType];
      }
    }
    if (propertyTypeSubType.residential.land.selected) {
      result.land = [];
    }
  }

  // for each commercial
  for (const [propertyType, data] of Object.entries(propertyTypeSubType.commercial)) {
    if (propertyType === '') continue;
    if (data.selected) {
      result[propertyType as keyof SearchFilterRequestPropertyTypeSubType] = [];
    }
  }

  if (objectTypeCategory === 'development') {
    result.group = [];
  }

  return result;
};

const isPropertySubTypeSelected = (
  propertyTypeSubType: SearchFilterRequestPropertyTypeSubType | undefined,
  propertySubType: PropertySubType[] | undefined,
  propertyType: keyof SearchFilterRequestPropertyTypeSubType,
  subType: PropertySubType,
): boolean => {
  // Reason: we currently support the new `propertyTypeSubType` shape, but also have to
  // maintain a fallback to the deprecated `propertySubType` array until it is removed.
  if (propertyTypeSubType) {
    return propertyTypeSubType[propertyType]?.length === 0 ? true : Boolean(propertyTypeSubType[propertyType]?.includes(subType));
  }
  return Boolean(propertySubType?.includes(subType));
};

const buildPropertySubTypeRecord = (
  propertyType: keyof SearchFilterRequestPropertyTypeSubType,
  subTypes: ReadonlyArray<PropertySubType>,
  propertyTypeSubType?: SearchFilterRequestPropertyTypeSubType,
  propertySubType?: PropertySubType[],
): PropertySubTypeRecord => {
  return subTypes.reduce<PropertySubTypeRecord>((acc, subType) => {
    acc[subType] = {
      selected: isPropertySubTypeSelected(propertyTypeSubType, propertySubType, propertyType, subType),
    };
    return acc;
  }, {});
};

// Mapping function to create PropertyTypeState structure from propertyTypeSubType or propertyType and propertySubType arrays
export const getSelectedPropertyTypeState = (
  propertyTypeSubType?: SearchFilterRequestPropertyTypeSubType,
  propertyType?: PropertyType[],
  propertySubType?: PropertySubType[],
): PropertyTypeState => {
  const propertyTypeState: PropertyTypeState = {
    residential: {},
    commercial: {},
    development: {},
  };

  // TODO: remove fallback once deprecated filters are removed
  const propertyTypes = propertyTypeSubType ? Object.keys(propertyTypeSubType) : (propertyType ?? []);

  // Residential Property Types
  propertyTypeState.residential['house'] = {
    selected: propertyTypeSubType ? propertyTypeSubType[PropertyType.house]?.length === 0 : propertyTypes.includes(PropertyType.house),
    subTypes: buildPropertySubTypeRecord(PropertyType.house, orderedResidentialHouseSubTypes, propertyTypeSubType, propertySubType),
  };

  propertyTypeState.residential['apartment'] = {
    selected: propertyTypeSubType
      ? propertyTypeSubType[PropertyType.apartment]?.length === 0
      : propertyTypes.includes(PropertyType.apartment),
    subTypes: buildPropertySubTypeRecord(PropertyType.apartment, APARTMENT_SUB_TYPES, propertyTypeSubType, propertySubType),
  };

  propertyTypeState.residential['land'] = {
    selected: propertyTypeSubType ? propertyTypeSubType[PropertyType.land]?.length === 0 : propertyTypes.includes(PropertyType.land),
    subTypes: buildPropertySubTypeRecord(PropertyType.land, orderedResidentialLandSubTypes, propertyTypeSubType, propertySubType),
  };

  for (const type of [PropertyType.house, PropertyType.apartment, PropertyType.land]) {
    const propertyTypeRecord = propertyTypeState.residential[type as keyof PropertyTypeRecord];
    if (!propertyTypeRecord) {
      continue;
    }

    let isSelected = propertyTypeRecord.selected;

    if (isSelected) {
      for (const subType of Object.values(propertyTypeRecord.subTypes ?? {})) {
        if (!subType.selected) {
          isSelected = false;
        }
      }
    }
    propertyTypeRecord.selected = isSelected;
  }

  // Commercial Property Types
  propertyTypeState.commercial = Object.fromEntries(
    orderedCommercialPropertyTypes.map((propertyType) => [propertyType, { selected: propertyTypes.includes(propertyType) }]),
  );
  return propertyTypeState;
};
